import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEUFabricStore } from "../store/euFabricStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";

/**
 * EU Scope Exchange Fabric (v4)
 * 
 * UI for managing EU-wide scope exchange fabric, replication lag,
 * reconciliation errors, and escalation levels.
 */
export default function EUFabric() {
  const { t } = useTranslation();
  const {
    memberStates,
    replicationLagMS,
    reconciliationErrors,
    fabricHealthScore,
    interopVersion,
    crossBorderPackets,
    anomalyFlags,
    escalationLevel,
    setMemberStates,
    setCrossBorderPackets,
    setInteropVersion,
    simulateReplication,
    triggerEscalation,
    exportFabricEnvelope,
  } = useEUFabricStore();

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("EUFabricPanel");
  }, []);

  // Derived values - must be defined before useEffect that uses them
  const interopPenalty = useEUFabricStore((s) => s.getInteropPenalty());
  const borderFriction = useEUFabricStore((s) => s.getBorderFriction());
  const escalationIndex = useEUFabricStore((s) => s.getEscalationIndex());
  const fabricStatus = useEUFabricStore((s) => s.getFabricStatus());

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Update AI context inputs on change
  useEffect(() => {
    useAIContext.getState().setInput("memberStates", memberStates);
    useAIContext.getState().setInput("replicationLagMS", replicationLagMS);
    useAIContext.getState().setInput("reconciliationErrors", reconciliationErrors);
    useAIContext.getState().setInput("fabricHealthScore", fabricHealthScore);
    useAIContext.getState().setInput("crossBorderPackets", crossBorderPackets);
    useAIContext.getState().setInput("anomalyFlags", anomalyFlags);
    useAIContext.getState().setInput("escalationLevel", escalationLevel);
    useAIContext.getState().setInput("fabricStatus", fabricStatus);
    useAIContext.getState().setInput("interopPenalty", interopPenalty);
    useAIContext.getState().setInput("borderFriction", borderFriction);
  }, [memberStates, replicationLagMS, reconciliationErrors, fabricHealthScore, crossBorderPackets, anomalyFlags, escalationLevel, fabricStatus, interopPenalty, borderFriction]);

  const handleSimulate = () => {
    setError(null);
    try {
      simulateReplication();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("euFabric.simulateFailed") || "Failed to simulate replication");
      setError(error);
      console.error("Simulate replication failed:", e);
    }
  };

  const handleEscalation = () => {
    setError(null);
    try {
      triggerEscalation();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("euFabric.escalationFailed") || "Failed to trigger escalation");
      setError(error);
      console.error("Trigger escalation failed:", e);
    }
  };

  const handleExport = () => {
    setError(null);
    try {
      exportFabricEnvelope();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("euFabric.exportFailed") || "Failed to export fabric envelope");
      setError(error);
      console.error("Export failed:", e);
    }
  };

  return (
    <div className="space-y-6">
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("euFabric.title") || "EU Scope Exchange Fabric"}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />

      {/* Input Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("euFabric.memberStates") || "Member States (comma-separated)"}
          </label>
          <input
            type="text"
            value={memberStates.join(", ")}
            onChange={(e) => {
              const states = e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter((s) => s.length > 0);
              setMemberStates(states);
            }}
            placeholder="e.g., DE, FR, IT, ES"
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("euFabric.crossBorderPackets") || "Cross-Border Packets"}
          </label>
          <input
            type="number"
            min="0"
            value={crossBorderPackets}
            onChange={(e) => setCrossBorderPackets(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("euFabric.interopVersion") || "Interoperability Version"}
          </label>
          <input
            type="text"
            value={interopVersion}
            onChange={(e) => setInteropVersion(e.target.value)}
            placeholder="e.g., 1.0.0"
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("euFabric.fabricHealthScore") || "Fabric Health Score (0-100)"}
          </label>
          <input
            type="number"
            min="0"
            max="100"
            value={fabricHealthScore}
            onChange={(e) => {
              const score = Math.max(0, Math.min(100, Number(e.target.value)));
              useEUFabricStore.setState({ fabricHealthScore: score });
            }}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleSimulate}
          disabled={loading}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("euFabric.simulate") || "Simulate Replication"}
        </button>
        <button
          onClick={handleEscalation}
          disabled={loading}
          className="px-4 py-2 bg-evat-neon/20 border border-evat-neon text-evat-neon rounded hover:bg-evat-neon/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("euFabric.triggerEscalation") || "Trigger Escalation"}
        </button>
      </div>

      {/* Metrics Display */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("euFabric.replicationLagMS") || "Replication Lag"}
          </div>
          <div className="text-lg font-orbitron text-evat-gold">
            {replicationLagMS}ms
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("euFabric.reconciliationErrors") || "Reconciliation Errors"}
          </div>
          <div className="text-lg font-orbitron text-red-400">
            {reconciliationErrors}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("euFabric.anomalyFlags") || "Anomaly Flags"}
          </div>
          <div className="text-lg font-orbitron text-yellow-400">
            {anomalyFlags}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("euFabric.escalationLevel") || "Escalation Level"}
          </div>
          <div className={`text-lg font-orbitron ${
            escalationLevel === "critical" ? "text-red-400" :
            escalationLevel === "warning" ? "text-yellow-400" :
            "text-green-400"
          }`}>
            {escalationLevel.toUpperCase()}
          </div>
        </div>
      </div>

      {/* Derived Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("euFabric.interopPenalty") || "Interop Penalty"}
          </div>
          <div className="text-sm font-orbitron text-orange-400">
            {interopPenalty.toFixed(2)}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("euFabric.borderFriction") || "Border Friction"}
          </div>
          <div className="text-sm font-orbitron text-purple-400">
            {borderFriction.toFixed(2)}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("euFabric.fabricStatus") || "Fabric Status"}
          </div>
          <div className={`text-lg font-orbitron ${
            fabricStatus >= 80 ? "text-green-400" :
            fabricStatus >= 50 ? "text-yellow-400" :
            "text-red-400"
          }`}>
            {fabricStatus.toFixed(1)}%
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("euFabric.interopVersion") || "Interop Version"}
          </div>
          <div className="text-sm font-orbitron text-evat-neon">
            {interopVersion}
          </div>
        </div>
      </div>

      {/* Export Button */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleExport}
          disabled={loading}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("euFabric.export") || "Export Fabric Envelope"}
        >
          {t("euFabric.export") || "Export Fabric Envelope"}
        </button>
      </div>
    </div>
  );
}

